import {
  Box,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Input from "../../../../components/Input";
import ButtonComp from "../../../../components/Button";
import { getOperationalLocations } from "../../../../services/dashboard";
import { useCaseCreationStore } from "../../../../store/useCaseCreationStore";
import {
  caseActionBarSx,
  caseButtonSx,
  caseFormCardSx,
  caseGridSx,
  caseLabelSx,
  caseMultilineSx,
} from "./caseFormStyles";

const SOUTH_AFRICA_PROVINCES = [
  "Gauteng",
  "Western Cape",
  "KwaZulu-Natal",
  "Eastern Cape",
  "Free State",
  "Limpopo",
  "Mpumalanga",
  "North West",
  "Northern Cape",
];

export default function BodyLocationForm({
  onNext,
  onBack,
  deceasedLocation,
  setDeceasedLocation,
  minimalCapture = false,
  hideActions = false,
  validationRef,
}: any) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { mortuaryName, setMortuaryName } = useCaseCreationStore();
  const [mortuaryOptions, setMortuaryOptions] = useState<any[]>([]);
  const [mortuariesLoading, setMortuariesLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setMortuariesLoading(true);
    void getOperationalLocations("MORTUARY").then((response: any) => {
      if (!isMounted) return;
      setMortuaryOptions(response?.status === 200 && Array.isArray(response.data) ? response.data : []);
      setMortuariesLoading(false);
    }).catch(() => {
      if (isMounted) setMortuariesLoading(false);
    });
    return () => { isMounted = false; };
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!mortuaryName?.trim()) newErrors.mortuaryName = "Mortuary name is required";

    if (!deceasedLocation.streetName)
      newErrors.streetName = "Street name is required";

    if (!deceasedLocation.suburb) newErrors.suburb = "Suburb is required";

    if (!deceasedLocation.city) newErrors.city = "City is required";

    if (!deceasedLocation.postalCode) {
      newErrors.postalCode = "Postal code is required";
    } else if (!/^\d{4}$/.test(deceasedLocation.postalCode)) {
      newErrors.postalCode = "Postal code must be exactly 4 digits";
    }

    if (!deceasedLocation.province) newErrors.province = "Province is required";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleNextClick = () => {
    const isValid = validateForm();

    if (!isValid) return;

    onNext();
  };

  if (validationRef) {
    validationRef.current = validateForm;
  }

  const clearError = (field: string) => {
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  return (
    <Paper
      elevation={0}
      sx={caseFormCardSx}
    >
      <Grid container spacing={2.25} sx={caseGridSx}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            select
            fullWidth
            label="Mortuary Name *"
            variant="outlined"
            value={mortuaryName || ""}
            onChange={(e: any) => {
              setMortuaryName(e.target.value);
              clearError("mortuaryName");
            }}
            error={Boolean(errors.mortuaryName)}
            helperText={errors.mortuaryName}
          >
            <MenuItem value="" disabled>Select Mortuary</MenuItem>
            {mortuariesLoading && <MenuItem disabled>Loading mortuaries...</MenuItem>}
            {mortuaryOptions.map((mortuary: any) => (
              <MenuItem key={mortuary.id || mortuary.externalBranchId || mortuary.branchName} value={mortuary.branchName}>
                {mortuary.branchName}
              </MenuItem>
            ))}
          </Input>
        </Grid>
        {/* First Name */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            fullWidth
            label="House / Unit Number (optional)"
            placeholder="House/Unit Number"
            variant="outlined"
            value={deceasedLocation.houseNumber}
            onChange={(e: any) => {
              setDeceasedLocation({ houseNumber: e.target.value });
              clearError("houseNumber");
            }}
            error={Boolean(errors.houseNumber)}
            helperText={errors.houseNumber}
          />
        </Grid>

        {/* Last Name */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            fullWidth
            label="Street Name *"
            placeholder="Street Name"
            variant="outlined"
            value={deceasedLocation.streetName}
            onChange={(e: any) => {
              setDeceasedLocation({ streetName: e.target.value });
              clearError("streetName");
            }}
            error={Boolean(errors.streetName)}
            helperText={errors.streetName}
          />
        </Grid>

        {/* Identity Type */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            fullWidth
            label="Suburb / Area *"
            placeholder="Suburb/Area"
            variant="outlined"
            value={deceasedLocation.suburb}
            onChange={(e: any) => {
              setDeceasedLocation({ suburb: e.target.value });
              clearError("suburb");
            }}
            error={Boolean(errors.suburb)}
            helperText={errors.suburb}
          />
        </Grid>

        {/* Identity Number */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            fullWidth
            label="City / Town *"
            placeholder="City/Town"
            variant="outlined"
            value={deceasedLocation.city}
            onChange={(e: any) => {
              setDeceasedLocation({ city: e.target.value });
              clearError("city");
            }}
            error={Boolean(errors.city)}
            helperText={errors.city}
          />
        </Grid>

        {/* Mobile Number */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            select
            fullWidth
            label="Province *"
            placeholder="Province"
            variant="outlined"
            value={deceasedLocation.province}
            onChange={(e: any) => {
              setDeceasedLocation({ province: e.target.value });
              clearError("province");
            }}
            error={Boolean(errors.province)}
            helperText={errors.province}
          >
            <MenuItem value="" disabled>
              Select Province
            </MenuItem>
            {SOUTH_AFRICA_PROVINCES.map((province) => (
              <MenuItem key={province} value={province}>
                {province}
              </MenuItem>
            ))}
          </Input>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            fullWidth
            label="Postal Code *"
            placeholder="Postal Code"
            variant="outlined"
            value={deceasedLocation.postalCode}
            onChange={(e: any) => {
              const value = e.target.value;

              if (/^\d{0,4}$/.test(value)) {
                setDeceasedLocation({ postalCode: value });
                clearError("postalCode");
              }
            }}
            error={Boolean(errors.postalCode)}
            helperText={errors.postalCode}

          />
        </Grid>
        {/* <Grid size={{ xs: 12, md: 6 }}>
          <Input
            fullWidth
            label="Location Type *"
            variant="outlined"
            value={deceasedLocation.locationType}
            onChange={(e: any) => {
              setDeceasedLocation({ locationType: e.target.value });
              clearError("locationType")
            }}
            error={Boolean(errors.locationType)}
            helperText={errors.locationType}
          />
        </Grid> */}
        <Grid size={{ xs: 12 }}>
          <Typography sx={caseLabelSx}>
            Notes / Remark (optional)
          </Typography>
          <TextField
            multiline
            rows={5}
            fullWidth
            variant="outlined"
            placeholder="Notes/Remark"
            value={deceasedLocation.notes}
            onChange={(e: any) => {
              setDeceasedLocation({ notes: e.target.value });
              clearError("notes");
            }}
            error={Boolean(errors.notes)}
            helperText={errors.notes}
            sx={caseMultilineSx}
          />
        </Grid>
      </Grid>

      {!hideActions && (
        <Box sx={caseActionBarSx}>
          <ButtonComp variant="outlined" sx={caseButtonSx} onClick={onBack}>
            Back
          </ButtonComp>

          <ButtonComp
            variant="contained"
            sx={caseButtonSx}
            onClick={handleNextClick}
          >
            Next
          </ButtonComp>
        </Box>
      )}
    </Paper>
  );
}
